import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

import {
  DEFAULT_PEAR_EYE_GRADE_FORM,
  DEFAULT_PEAR_EYE_GRADE_FORM_HYPERTENSIVE,
  EYE_MAP,
  DEFAULT_STATE,
  LATERALITY_MAP,
} from './default-data';
import { GradeFlowState } from './types';

export const useGradeFlow = create<GradeFlowState>()(
  immer((set) => ({
    ...DEFAULT_STATE,
    setFlowBlocked: (blocked) => {
      set((state) => {
        state.isFlowBlocked = blocked;
      });
    },
    setCaseValidClaimed: (claimed) => {
      set((state) => {
        state.isCaseValidClaimed = claimed;
        state.claimedAt = new Date();
      });
    },
    initializeFlow: (_case) => {
      set((state) => {
        state.case = _case;
        state.form.case_id = _case.case_id;
        state.images = _case.images.map((img) => ({
          image: img.image,
          url: img.url,
          lat: assignLateralityToImages(img.image),
        }));
        state.form.images_readable = _case.images.map((img) => ({
          img: img.image,
          is_readable: '',
        }));
        state.isLateralityPresentOnImgs = _case.images.some(
          (img) => img.image.includes('OD') || img.image.includes('OS')
        );

        // TODO: we should have some logic based on the order in the case to enable the forms
        state.forms = ['diabetic'];
      });
    },
    setImageReadable: (img, isReadable) => {
      set((state) => {
        if (!state.form.images_readable) return;
        state.form.images_readable = state.form.images_readable.map((i) =>
          i.img === img ? { img, is_readable: isReadable ? 'yes' : 'no' } : i
        );
      });
    },
    setIsReadable: (isReadable) => {
      set((state) => {
        state.form.is_readable = isReadable;
      });
    },
    // DIABETIC
    // questions pear eye
    setQuestionEvidenceDme: (evidenciaDme, eye) => {
      set((state) => {
        const eyeToGrade = EYE_MAP[eye];
        if (!state.form[eyeToGrade]) {
          state.form[eyeToGrade] = {
            ...DEFAULT_PEAR_EYE_GRADE_FORM,
            evidence_of_dme: evidenciaDme,
          };
          return;
        }
        state.form[eyeToGrade].evidence_of_dme = evidenciaDme;
      });
    },
    setQuestionDrGrade: (grade_scale, eye) => {
      set((state) => {
        const eyeToGrade = EYE_MAP[eye];
        state.form[eyeToGrade] ??= {
          ...DEFAULT_PEAR_EYE_GRADE_FORM,
          dr_grade_scale: grade_scale,
        };
        state.form[eyeToGrade].dr_grade_scale = grade_scale;
      });
    },
    setQuestionScaleValue: (selected, scale, eye) => {
      set((state) => {
        // We were getting a typescript issue so we had to add this switch statement
        switch (scale.segment) {
          case 'scale_10':
            state.form[EYE_MAP[eye]]!.scale_10[scale.key] = selected;
            break;
          case 'scale_20_35':
            state.form[EYE_MAP[eye]]!.scale_20_35[scale.key] = selected;
            break;
          case 'scale_43':
            state.form[EYE_MAP[eye]]!.scale_43[scale.key] = selected;
            break;
          case 'scale_47':
            state.form[EYE_MAP[eye]]!.scale_47[scale.key] = selected;
            break;
          case 'scale_53':
            state.form[EYE_MAP[eye]]!.scale_53[scale.key] = selected;
            break;
          case 'scale_61':
            state.form[EYE_MAP[eye]]!.scale_61[scale.key] = selected;
            break;
          case 'scale_65':
            state.form[EYE_MAP[eye]]!.scale_65[scale.key] = selected;
            break;
          case 'scale_71_75':
            state.form[EYE_MAP[eye]]!.scale_71_75[scale.key] = selected;
            break;
          case 'scale_81_85':
            state.form[EYE_MAP[eye]]!.scale_81_85[scale.key] = selected;
            break;
          default:
            break;
        }
      });
    },
    clearProliferativeValues: (eye) => {
      set((state) => {
        const eyeToGrade = EYE_MAP[eye];
        state.form[eyeToGrade]!.scale_61.nve_greater_than_half_da_in_1_plus_quadrants_61 = false;
        state.form[eyeToGrade]!.scale_65.nve_greater_than_half_da_in_1_plus_quadrants_65 = false;
        state.form[eyeToGrade]!.scale_65.nvd_less_than_three_quarters = false;
        state.form[eyeToGrade]!.scale_71_75.larger_nvd_or_nve_greater_than_or_equal_to_half_da =
          false;
        state.form[eyeToGrade]!.scale_81_85.view_partially_obscured_by_vh = false;
      });
    },
    clearSevereValues: (eye) => {
      set((state) => {
        const eyeToGrade = EYE_MAP[eye];
        state.form[eyeToGrade]!.scale_53.severe_rhs_in_4_quadrants = false;
        state.form[eyeToGrade]!.scale_53.moderate_to_severe_irma_in_1_plus_quadrants = false;
        state.form[eyeToGrade]!.scale_53.venous_beading_in_2_plus_quadrants = false;

        state.clearProliferativeValues(eye);
      });
    },
    clearModerateValues: (eye) => {
      set((state) => {
        const eyeToGrade = EYE_MAP[eye];
        state.form[eyeToGrade]!.scale_47.moderate_rhs_plus_mild_irma = false;
        state.form[eyeToGrade]!.scale_47.mild_irma_in_4_quadrants = false;
        state.form[eyeToGrade]!.scale_47.severe_rhs_in_2_to_3_quadrants = false;
        state.form[eyeToGrade]!.scale_47.venous_beading_in_1_quadrant = false;

        state.clearSevereValues(eye);
      });
    },
    clearMildValues: (eye) => {
      set((state) => {
        const eyeToGrade = EYE_MAP[eye];
        state.form[eyeToGrade]!.scale_20_35.microaneurysms = false;
        state.form[eyeToGrade]!.scale_20_35.hard_exudates = false;
        state.form[eyeToGrade]!.scale_20_35.cws = false;
        state.form[eyeToGrade]!.scale_20_35.mild_rhs = false;

        state.clearModerateValues(eye);
      });
    },
    // HYPERTENSIVE
    setInitialHypertensiveForm: () => {
      set((state) => {
        // if is already initialized, do nothing
        if (
          Boolean(state.form.grade_hypertensive?.left_eye) ||
          Boolean(state.form.grade_hypertensive?.right_eye) ||
          Boolean(state.form.grade_hypertensive?.both_eyes)
        ) {
          return;
        }

        if (state.isLateralityPresentOnImgs) {
          state.form.grade_hypertensive = {
            left_eye: DEFAULT_PEAR_EYE_GRADE_FORM_HYPERTENSIVE,
            right_eye: DEFAULT_PEAR_EYE_GRADE_FORM_HYPERTENSIVE,
          };
        } else {
          state.form.grade_hypertensive = {
            both_eyes: DEFAULT_PEAR_EYE_GRADE_FORM_HYPERTENSIVE,
          };
        }
      });
    },
    setHypertensiveQuestionScaleValue: (selected, scale, eye) => {
      set((state) => {
        const eyeToGrade = LATERALITY_MAP[eye];

        // Initialize the grade_hypertensive form if it doesn't exist hypertensive
        if (!state.form?.grade_hypertensive) {
          state.form.grade_hypertensive = {
            both_eyes: DEFAULT_PEAR_EYE_GRADE_FORM_HYPERTENSIVE,
            left_eye: DEFAULT_PEAR_EYE_GRADE_FORM_HYPERTENSIVE,
            right_eye: DEFAULT_PEAR_EYE_GRADE_FORM_HYPERTENSIVE,
          };
          state.form.grade_hypertensive[eyeToGrade] = {
            ...DEFAULT_PEAR_EYE_GRADE_FORM_HYPERTENSIVE,
            [scale.segment]: {
              ...DEFAULT_PEAR_EYE_GRADE_FORM_HYPERTENSIVE[scale.segment],
              [scale.key]: selected,
            },
          };
          return;
        }

        // now check if the eye side exists in the hypertensive form
        if (!state.form.grade_hypertensive[eyeToGrade]) {
          state.form.grade_hypertensive[eyeToGrade] = {
            ...DEFAULT_PEAR_EYE_GRADE_FORM_HYPERTENSIVE,
            [scale.segment]: {
              ...DEFAULT_PEAR_EYE_GRADE_FORM_HYPERTENSIVE[scale.segment],
              [scale.key]: selected,
            },
          };
          return;
        }

        // if the eye side exists, just update the scale value
        switch (scale.segment) {
          case 'grade_i':
            state.form.grade_hypertensive[eyeToGrade].grade_i![scale.key] = selected;
            break;
          case 'grade_ii':
            state.form.grade_hypertensive[eyeToGrade].grade_ii![scale.key] = selected;
            break;
          case 'grade_iii':
            state.form.grade_hypertensive[eyeToGrade].grade_iii![scale.key] = selected;
            break;
          case 'grade_iv':
            state.form.grade_hypertensive[eyeToGrade].grade_iv![scale.key] = selected;
            break;
          default:
            break;
        }
      });
    },
    setSignature: (signature) => {
      set((state) => {
        state.form.signature_data_uri = signature;
      });
    },
    setReferralDrFollowUp: (selected) => {
      set((state) => {
        state.form.referral_dr_follow_up = selected;
      });
    },
    setUrgentCase: (urgent) => {
      set((state) => {
        state.form.is_urgent_case = urgent;
      });
    },
    setIsOtherDiseaseNoneSelected: (selected) => {
      set((state) => {
        state.isOtherDiseaseNoneSelected = selected;
        // reset all the other diseases if none is selected
        if (selected) {
          state.form.other_disease = DEFAULT_STATE.form.other_disease;
          state.isOtherCustomDiseaseSelected = false;
        }
      });
    },
    setIsOtherCustomDiseaseSelected: (selected) => {
      set((state) => {
        state.isOtherCustomDiseaseSelected = selected;
        state.isOtherDiseaseNoneSelected = false;
      });
    },
    setOtherDiseaseValue: (key, value) => {
      set((state) => {
        state.form.other_disease![key] = value;
        state.isOtherDiseaseNoneSelected = false;
      });
    },
    setCustomDiseaseValue: (value) => {
      set((state) => {
        state.form.other_disease!.custom = value;
        state.isOtherDiseaseNoneSelected = false;
      });
    },
    setOtherDiseaseFollowUp: (value) => {
      set((state) => {
        state.form.other_disease!.follow_up = value;
      });
    },
    setICDCodeSelected: (icd: string) => {
      set((state) => {
        // validate if the icd code is already in the list
        const icdCode = state.form.icd_10_codes?.find((code) => code === icd);
        if (icdCode && state.form.icd_10_codes) {
          state.form.icd_10_codes = state.form.icd_10_codes.filter((code) => code !== icd);
          return;
        }
        // the list is already initialized
        if (state.form.icd_10_codes) {
          state.form.icd_10_codes.push(icd);
          return;
        }
        // the list is not initialized
        state.form.icd_10_codes = [icd];
      });
    },
    setICDCodesSelected: (icds: string[]) => {
      set((state) => {
        state.form.icd_10_codes = icds;
      });
    },
    setIsSavingGrade: (saving) => {
      set((state) => {
        state.isSavingGrade = saving;
      });
    },
    setLayerImages: (imgs) => {
      set((state) => {
        state.layerImages = imgs;
      });
    },
    setIsLoadingLayerImages: (loading) => {
      set((state) => {
        state.isLoadingLayerImages = loading;
      });
    },
    setImageSegmentationData: (data) => {
      set((state) => {
        state.imageSegmentationData = data;
      });
    },
    setIsLoadingImageSegmentationData: (loading: boolean) => {
      set((state) => {
        state.isLoadingImageSegmentationData = loading;
      });
    },
    reset: () => {
      set((state) => {
        Object.assign(state, DEFAULT_STATE);
      });
    },
  }))
);

function assignLateralityToImages(img: string) {
  if (img.includes('OD')) {
    return 'right';
  }
  if (img.includes('OS')) {
    return 'left';
  }

  return undefined;
}
